import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import NumberTextField from 'core/components/NumberTextField';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Button from 'pages/Dashboard/components/Button';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  useGetPatientAppointmentChangeSettings,
  useUpdatePatientAppointmentChangeSettings,
} from 'views/EMR/Settings/hooks';
import {
  useNotify,
} from 'core/hooks';
import {
  maxPatientAppointmentChangeCutoffHours,
  parsePatientAppointmentChangeCutoffHours,
} from 'views/EMR/Settings/Appointments/patientAppointmentChangeSettings';

export default function PatientSelfServiceSettings() {
  const notify = useNotify();
  const {
    data,
    isLoading,
    refetch,
  } = useGetPatientAppointmentChangeSettings();
  const {
    mutateAsync: updateSettings,
    isPending,
  } = useUpdatePatientAppointmentChangeSettings();
  const [value, setValue] = useState('');

  useEffect(() => {
    const cutoffHours = data?.patientAppointmentChangeCutoffHours;
    setValue(cutoffHours == null ? '' : String(cutoffHours));
  }, [data?.patientAppointmentChangeCutoffHours]);

  const parsedValue = useMemo(
    () => parsePatientAppointmentChangeCutoffHours(value),
    [value],
  );

  const savedValue = data?.patientAppointmentChangeCutoffHours ?? null;
  const isValid = parsedValue !== undefined;
  const isDirty = isValid && parsedValue !== savedValue;

  const onSave = async () => {
    if (!isValid) return;

    try {
      await updateSettings({
        patientAppointmentChangeCutoffHours: parsedValue,
      });
      await refetch();
      notify({
        message: 'Patient self-service appointment settings have been updated.',
        variant: 'success',
      });
    } catch (error) {
      console.error(error);
    }
  };

  return isLoading ? (
    <div className="flex flex-1 items-center justify-center">
      <Spinner />
    </div>
  ) : (
    <div className="flex flex-1 overflow-auto px-4 pb-4">
      <div className="flex w-full max-w-2xl flex-col gap-4 rounded border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-semibold">Patient self-service appointment changes</h3>
          <p className="text-xs text-gray-600">
            Restricts patient online cancellation and callback requests within this many hours of the appointment.
            Leave blank or enter 0 for no advance cutoff. Patients still cannot cancel or request rescheduling at or
            after the appointment start.
          </p>
          <p className="text-xs text-gray-600">
            Staff can continue changing appointments at any time under existing staff rules. This setting does not
            change reminder timing.
          </p>
        </div>
        <Wrapper
          id="patient-appointment-change-cutoff-hours"
          label="Patient cancellation/rescheduling cutoff (hours)"
          error={!isValid}
          errorText={!isValid ? 'Enter a non-negative whole number of hours or leave the field blank.' : undefined}
        >
          <NumberTextField
            id="patient-appointment-change-cutoff-hours"
            value={value}
            onChange={(nextValue) => { setValue(nextValue == null ? '' : String(nextValue)); }}
            maskExtension={{ min: 0, max: maxPatientAppointmentChangeCutoffHours, scale: 0 }}
            placeholder="No advance cutoff"
          />
        </Wrapper>
        <div>
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            disabled={!isValid || !isDirty || isPending}
          >
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
